import { createSwiper } from '@nulo/swiper';

const MAX_PER_VIEW = 4;

/**
 * Ambassador product picks — createSwiper from @nulo/swiper.
 *
 * - 1–3 slides: slidesPerView = count (fill 100% width, no empty slot)
 * - 4+ slides: slidesPerView = 4
 *
 * @extends {HTMLElement}
 */
class AmbassadorRelatedProducts extends HTMLElement {
  /** @type {import('swiper').Swiper | null} */
  #swiper = null;

  /** @type {ResizeObserver | null} */
  #resizeObserver = null;

  connectedCallback() {
    if (this._initialized) return;
    this._initialized = true;

    this.#initSwiper();
  }

  disconnectedCallback() {
    this.#resizeObserver?.disconnect();
    this.#resizeObserver = null;
    this.#swiper?.destroy?.(true, true);
    this.#swiper = null;
    this._initialized = false;
  }

  #initSwiper() {
    const carousel = this.querySelector('[data-nulo-carousel]');
    const swiperEl = carousel?.querySelector('[data-swiper]');
    if (!(carousel instanceof HTMLElement) || !(swiperEl instanceof HTMLElement)) return;

    const slideCount =
      swiperEl.querySelectorAll('.swiper-slide').length ||
      Number.parseInt(this.dataset.slideCount || '0', 10);

    if (slideCount < 2) return;

    const prevEl = carousel.querySelector('[data-related-prev]');
    const nextEl = carousel.querySelector('[data-related-next]');

    // Exactly: ≤3 fill the row; >3 always show 4 per view
    const desktopPerView = slideCount > 3 ? MAX_PER_VIEW : slideCount;

    /** @type {Record<string, unknown>} */
    const config = {
      speed: 500,
      slidesPerView: slideCount > 3 ? 1.5 : Math.min(slideCount, 1.5),
      slidesPerGroup: 1,
      spaceBetween: 16,
      watchOverflow: true,
      watchSlidesProgress: true,
      observer: true,
      observeParents: true,
      allowTouchMove: true,
      breakpoints: {
        768: {
          slidesPerView: slideCount > 3 ? 3 : slideCount,
          spaceBetween: 16,
        },
        992: {
          slidesPerView: desktopPerView,
          spaceBetween: 20,
        },
        1200: {
          slidesPerView: desktopPerView,
          spaceBetween: 24,
        },
        1440: {
          slidesPerView: desktopPerView,
          spaceBetween: 40,
        },
      },
    };

    if (prevEl instanceof HTMLElement && nextEl instanceof HTMLElement) {
      config.navigation = { nextEl, prevEl };
    }

    this.#swiper = createSwiper({
      region: carousel,
      el: swiperEl,
      activeSlideClass: 'swiper-slide-fully-visible',
      config,
    });

    this.#watchVisibility(carousel);
  }

  /**
   * @param {HTMLElement} carousel
   */
  #watchVisibility(carousel) {
    if (typeof ResizeObserver === 'undefined') return;

    this.#resizeObserver = new ResizeObserver(() => {
      if (carousel.clientWidth > 0) this.#swiper?.update();
    });
    this.#resizeObserver.observe(carousel);
  }
}

if (!customElements.get('ambassador-related-products')) {
  customElements.define('ambassador-related-products', AmbassadorRelatedProducts);
}
